import * as Clipboard from "expo-clipboard";
import { Check, Copy, Eye, EyeOff } from "lucide-react-native";
import { FC, useState } from "react";

import { HStack, Pressable, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";

export interface Credential {
  emailAddress: string;
  password: string;
}

const CredentialsList: FC<{ credentials: Credential[] }> = ({
  credentials,
}) => {
  const { i18n } = useLocalization();
  const [copied, setCopied] = useState<string | null>(null);
  const [visible, setVisible] = useState<Set<string>>(new Set());

  const toggleVisible = (emailAddress: string) =>
    setVisible((prev) => {
      const next = new Set(prev);
      if (!next.delete(emailAddress)) next.add(emailAddress);
      return next;
    });

  const copy = async ({ emailAddress, password }: Credential) => {
    await Clipboard.setStringAsync(password);
    setCopied(emailAddress);
  };

  return (
    <VStack className="gap-2">
      <VStack>
        <Text bold className="text-sm text-typography-900">
          {i18n.t("credentials.title")}
        </Text>
        <Text className="text-xs text-typography-500">
          {i18n.t("credentials.subtitle")}
        </Text>
      </VStack>
      <VStack className="rounded-xl border border-outline-100 overflow-hidden">
        <HStack className="px-4 py-2 bg-background-50 border-b border-outline-100">
          <Text className="flex-1 text-xs font-medium text-typography-400">
            {i18n.t("credentials.email")}
          </Text>
          <Text className="w-48 text-xs font-medium text-typography-400">
            {i18n.t("credentials.password")}
          </Text>
          <Text className="w-20" />
        </HStack>
        <VStack style={{ marginBottom: -1 }}>
          {credentials.map((credential) => {
            const isCopied = copied === credential.emailAddress;
            const isVisible = visible.has(credential.emailAddress);
            const Icon = isCopied ? Check : Copy;
            const VisibilityIcon = isVisible ? EyeOff : Eye;
            return (
              <HStack
                className="shrink-0 items-center px-4 py-2.5 border-b border-outline-100"
                key={credential.emailAddress}
              >
                <Text
                  className="flex-1 text-sm text-typography-900"
                  numberOfLines={1}
                >
                  {credential.emailAddress}
                </Text>
                <HStack className="w-48 items-center gap-2">
                  <Text className="flex-1 text-sm text-typography-900 font-mono">
                    {isVisible
                      ? credential.password
                      : "•".repeat(credential.password.length)}
                  </Text>
                  <Pressable
                    className="p-1 rounded-md hover:bg-background-100"
                    onPress={() => toggleVisible(credential.emailAddress)}
                  >
                    <VisibilityIcon color="#94a3b8" size={14} />
                  </Pressable>
                </HStack>
                <Pressable
                  className="w-20 flex-row items-center justify-end gap-1.5"
                  onPress={() => copy(credential)}
                >
                  <Icon color={isCopied ? "#059669" : "#7c3aed"} size={14} />
                  <Text
                    className={`text-xs font-medium ${isCopied ? "text-emerald-600" : "text-violet-600"}`}
                  >
                    {isCopied
                      ? i18n.t("credentials.copied")
                      : i18n.t("credentials.copy")}
                  </Text>
                </Pressable>
              </HStack>
            );
          })}
        </VStack>
      </VStack>
    </VStack>
  );
};

export default CredentialsList;
